import type { CardInfo, Scan, ScanConfirmOptions } from "@hobbyshopify/backend-client";

/**
 * The confirm form's state and what it sends. Pure: the controller owns an
 * `EditState`, the form edits it, and these functions turn it into requests.
 *
 * The identity fields decide **which card this is**; they are the identity a
 * `Card` is keyed on, so editing one is what makes a corrected scan attach to
 * the right card (or create a new one) instead of quietly joining the wrong
 * one's price history.
 */
export type EditState = {
  // ── identity ──
  player: string;
  team: string;
  brand: string;
  year: string;
  cardNumber: string;
  parallelType: string;
  insertName: string;
  cardType: string;
  // Free-form, exactly as the model writes it: "PSA 10", "9.5", "raw".
  // scan-core's normalizeGrade parses it into (company, grade, slabbed), which
  // is why this stays one text field rather than three controls.
  grade: string;
  slabbed: boolean;
  // ── description ──
  cardFullName: string;
  setName: string;
  // ── economics, in dollars as typed ──
  price: string;
  floorPrice: string;
};

export function centsToInput(cents: number | null | undefined): string {
  return cents == null ? "" : (cents / 100).toFixed(2);
}

export function inputToCents(value: string): number | undefined {
  const parsed = Number(value);
  if (!value.trim() || !Number.isFinite(parsed)) return undefined;
  return Math.round(parsed * 100);
}

export function toEditState(scan: Scan): EditState {
  const info = scan.cardInfo;
  const card = scan.card;
  return {
    player: info?.player ?? "",
    team: info?.team ?? "",
    brand: info?.brand ?? "",
    year: info?.year != null ? String(info.year) : "",
    cardNumber: info?.card_number ?? "",
    parallelType: info?.parallel_type ?? "",
    insertName: info?.insert_name ?? "",
    cardType: info?.card_type ?? "",
    grade: info?.grade ?? "",
    slabbed: info?.slabbed ?? false,
    cardFullName: info?.card_full_name ?? "",
    setName: info?.set_name ?? "",
    // A matched card's own price seeds the form, so accepting a match keeps
    // what the shop already decided rather than re-asking for it. For a new
    // card there is no such decision yet, so the pipeline's estimate stands in:
    // that is the number the reviewer is being asked to confirm or correct.
    price: centsToInput(card?.currentPriceCents ?? scan.estimatedPriceCents),
    floorPrice: centsToInput(card?.floorPriceCents),
  };
}

export const MIN_YEAR = 1800;
export const MAX_YEAR = new Date().getFullYear();

/** Empty is allowed (the model may not have read a year); otherwise 1800..this year. */
export function isYearValid(year: string): boolean {
  if (!year) return true;
  if (!/^\d{4}$/.test(year)) return false;
  const parsed = Number(year);
  return parsed >= MIN_YEAR && parsed <= MAX_YEAR;
}

/** A floor above the asking price would refuse every sale it is meant to allow. */
export function isFloorValid(edit: EditState): boolean {
  const price = inputToCents(edit.price);
  const floor = inputToCents(edit.floorPrice);
  if (price == null || floor == null) return true;
  return floor <= price;
}

/**
 * Just the reading half: what this edit changes about the card itself.
 *
 * Diffed against `scan.cardInfo`, which is the reading *in force* including any
 * draft already saved, so reverting a field back to what the model said sends
 * that value rather than silently leaving the earlier edit in place. Only the
 * fields the reviewer actually changed: sending the whole reading back would
 * make every confirm look like a correction, and `matchedCardId <> cardId`,
 * the matcher's measured error rate, would stop meaning anything.
 *
 * Empty when nothing changed, which is what lets a caller skip the request.
 */
export function buildCorrections(scan: Scan, edit: EditState): Partial<CardInfo> {
  const info: Partial<CardInfo> = scan.cardInfo ?? {};
  const corrections: Record<string, unknown> = {};

  // A field the model did not read compares as the form's own empty value for
  // its type, so an untouched checkbox is not sent as a correction.
  const put = (key: keyof CardInfo, was: string | number | boolean | undefined, now: string | number | boolean) => {
    const empty = typeof now === "boolean" ? false : "";
    if ((was ?? empty) !== now) corrections[key] = now;
  };

  put("player", info.player, edit.player);
  put("team", info.team, edit.team);
  put("brand", info.brand, edit.brand);
  put("card_number", info.card_number, edit.cardNumber);
  put("parallel_type", info.parallel_type, edit.parallelType);
  put("insert_name", info.insert_name, edit.insertName);
  put("card_type", info.card_type, edit.cardType);
  put("grade", info.grade, edit.grade);
  put("slabbed", info.slabbed, edit.slabbed);
  put("card_full_name", info.card_full_name, edit.cardFullName);
  put("set_name", info.set_name, edit.setName);
  if (edit.year && Number(edit.year) !== info.year) corrections.year = Number(edit.year);

  return corrections as Partial<CardInfo>;
}

/**
 * What confirming sends: the identity corrections and the prices. Stock is
 * **not** here: whether the shop counts copies is step two, and the caller
 * merges it in; the presence of `stock` is what the backend reads as "track
 * this card".
 */
export function buildConfirmBody(scan: Scan, edit: EditState): ScanConfirmOptions {
  const corrections = buildCorrections(scan, edit);

  const body: ScanConfirmOptions = {};
  if (Object.keys(corrections).length > 0) body.corrections = corrections;

  const priceCents = inputToCents(edit.price);
  if (priceCents != null) body.priceCents = priceCents;
  const floorCents = inputToCents(edit.floorPrice);
  if (floorCents != null) body.floorPriceCents = floorCents;

  return body;
}
